import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { apiRequest } from '../../api/client';

interface DashboardStats {
  pendingJobs: number;
  pendingReviews: number;
  pendingPosts: number;
  totalJobs: number;
  publishedJobs: number;
  totalCompanies: number;
  totalUsers: number;
  totalPosts: number;
  activeTargets: number;
  postsBySource: Record<string, number>;
}

interface HealthStatus {
  status: 'checking' | 'healthy' | 'degraded' | 'offline';
  database?: string;
  uptime?: number;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const AdminDashboard: React.FC = () => {
  const { getToken } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<HealthStatus>({ status: 'checking' });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = await getToken();
        const data = await apiRequest<DashboardStats>('/admin/stats', {}, token);
        setStats(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const checkHealth = async () => {
      try {
        const response = await fetch(`${API_URL.replace('/api', '')}/health`);
        if (response.ok) {
          const data = await response.json();
          setHealth({
            status: data.status === 'healthy' ? 'healthy' : 'degraded',
            database: data.database,
            uptime: data.uptime,
          });
        } else {
          setHealth({ status: 'degraded' });
        }
      } catch {
        setHealth({ status: 'offline' });
      }
    };

    fetchStats();
    checkHealth();

    // Check health every 30 seconds
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, [getToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-cream-200/50">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-red-400">
        {error}
      </div>
    );
  }

  const statCards = [
    { label: 'Pending Posts', value: stats?.pendingPosts || 0, link: '/admin/posts', color: 'amber', icon: '📝' },
    { label: 'Pending Jobs', value: stats?.pendingJobs || 0, link: '/admin/jobs?approved=false', color: 'amber', icon: '📋' },
    { label: 'Published Jobs', value: stats?.publishedJobs || 0, link: '/admin/jobs?approved=true', color: 'green', icon: '✅' },
    { label: 'Total Jobs', value: stats?.totalJobs || 0, link: '/admin/jobs', color: 'blue', icon: '💼' },
    { label: 'Companies', value: stats?.totalCompanies || 0, link: '#', color: 'gray', icon: '🏢' },
    { label: 'Users', value: stats?.totalUsers || 0, link: '#', color: 'gray', icon: '👥' },
  ];

  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-amber-400">Dashboard Overview</h1>

        {/* Server Health Status */}
        <div className="flex items-center gap-3 bg-midnight-800/50 rounded-lg px-4 py-2 border border-cream-100/10">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${
              health.status === 'healthy' ? 'bg-green-400' :
              health.status === 'degraded' ? 'bg-yellow-400' :
              health.status === 'offline' ? 'bg-red-400' : 'bg-gray-400'
            }`}></span>
            <span className={`text-sm ${
              health.status === 'healthy' ? 'text-green-400' :
              health.status === 'degraded' ? 'text-yellow-400' :
              health.status === 'offline' ? 'text-red-400' : 'text-gray-400'
            }`}>
              {health.status === 'checking' ? 'Checking...' :
               health.status === 'healthy' ? 'Server Online' :
               health.status === 'degraded' ? 'Degraded' : 'Offline'}
            </span>
          </div>
          {health.uptime && (
            <span className="text-xs text-cream-200/50">
              Uptime: {formatUptime(health.uptime)}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <Link
            key={card.label}
            to={card.link}
            className="card-wood rounded-lg p-4 hover:border-amber-500/50 transition"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg">{card.icon}</span>
                <span className="text-cream-200/70 text-sm">{card.label}</span>
              </div>
              <div className="text-2xl font-bold text-cream-100">{card.value}</div>
            </div>
          </Link>
        ))}

        {/* Total Posts with breakdown */}
        <div className="card-wood rounded-lg p-4">
          <Link to="/admin/posts" className="block hover:opacity-80 transition mb-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg">📄</span>
                <span className="text-cream-200/70 text-sm">Total Posts</span>
              </div>
              <div className="text-2xl font-bold text-cream-100">{stats?.totalPosts || 0}</div>
            </div>
          </Link>

          {/* Posts by Source Breakdown */}
          {stats?.postsBySource && Object.keys(stats.postsBySource).length > 0 && (
            <div className="border-t border-cream-100/10 pt-3 mt-1 space-y-1.5">
              {Object.entries(stats.postsBySource)
                .sort(([, a], [, b]) => b - a)
                .map(([source, count]) => (
                  <div key={source} className="flex justify-between items-center">
                    <span className="text-xs text-cream-200/70 uppercase tracking-wide">{source}</span>
                    <span className="text-sm font-bold text-cream-100">{count}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-cream-100 mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/admin/posts"
            className="bg-amber-500/20 text-amber-400 px-4 py-2 rounded-lg hover:bg-amber-500/30 transition"
          >
            Review Pending Posts ({stats?.pendingPosts || 0})
          </Link>
          <Link
            to="/admin/targets"
            className="bg-purple-500/20 text-purple-400 px-4 py-2 rounded-lg hover:bg-purple-500/30 transition"
          >
            Manage Scraper Targets
          </Link>
          <Link
            to="/admin/jobs?approved=false"
            className="bg-blue-500/20 text-blue-400 px-4 py-2 rounded-lg hover:bg-blue-500/30 transition"
          >
            Review Unpublished Jobs
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
